import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme,
  Avatar,
} from '@mui/material';
import { Logout } from '@mui/icons-material';
import { BellIcon, BellRingingIcon, BellSlashIcon } from '@phosphor-icons/react';
import { User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { getDb, USER_ROLES } from '@watchlist/shared';
import { Logo } from '../widgets/Logo';
import { font } from '../../utils/fontHelper';
import {
  checkPushPermission,
  initializePushNotifications,
  isPushSupported,
} from '../../services/pushNotificationService';

interface AccessPendingScreenProps {
  user: User;
  onLogout: () => void;
  onAccessGranted: () => void;
}

const AccessPendingScreen: React.FC<AccessPendingScreenProps> = ({
  user,
  onLogout,
  onAccessGranted,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [notifState, setNotifState] = useState<'unknown' | 'granted' | 'denied' | 'prompt'>('unknown');
  const [enablingNotifs, setEnablingNotifs] = useState(false);

  // On mount: check permission state; if already granted, save token silently
  useEffect(() => {
    if (!isPushSupported()) {
      setNotifState('denied');
      return;
    }

    const init = async () => {
      const permission = await checkPushPermission();
      setNotifState(permission);
      if (permission === 'granted') {
        // Token wasn't saved during pending — save it now so the approval push is delivered
        try {
          await initializePushNotifications(user.uid);
        } catch (e) {
          console.warn('⚠️ Could not register push token for pending user:', e);
        }
      }
    };

    void init();
  }, [user.uid]);

  // Real-time listener for user profile role changes
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const setupListener = async () => {
      try {
        const db = await getDb();
        unsubscribe = onSnapshot(
          doc(db, 'userProfiles', user.uid),
          (snapshot) => {
            if (snapshot.exists()) {
              const data = snapshot.data();
              if (data?.role && data.role !== USER_ROLES.PENDING) {
                onAccessGranted();
              }
            }
          },
          (error) => {
            console.error('❌ Error listening to profile changes:', error);
          }
        );

      } catch (error) {
        console.error('❌ Error setting up real-time listener:', error);
      }
    };

    void setupListener();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user.uid, onAccessGranted]);

  const handleEnableNotifications = async () => {
    setEnablingNotifs(true);
    try {
      await initializePushNotifications(user.uid);
      // Re-check so UI reflects actual granted/denied outcome
      const permission = await checkPushPermission();
      setNotifState(permission);
    } catch (e) {
      console.warn('⚠️ Could not enable push notifications:', e);
      setNotifState('denied');
    } finally {
      setEnablingNotifs(false);
    }
  };

  const renderNotificationSection = () => {
    if (!isPushSupported() || notifState === 'unknown') return null;

    if (notifState === 'granted') {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mb: 4,
            py: 1.5,
            px: 2.5,
            bgcolor: isDark ? 'rgba(108, 100, 253, 0.1)' : 'rgba(108, 100, 253, 0.08)',
            borderRadius: 2,
            maxWidth: 340,
            mx: 'auto',
          }}
        >
          <BellRingingIcon size={18} color={theme.palette.primary.main} />
          <Typography
            sx={{
              fontSize: 13,
              color: theme.palette.primary.main,
              fontFamily: font(),
              fontWeight: 500,
            }}
          >
            You'll be notified the moment you're approved
          </Typography>
        </Box>
      );
    }

    if (notifState === 'denied') {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 4,
            py: 1.5,
            px: 2.5,
            bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
            borderRadius: 2,
            maxWidth: 360,
            mx: 'auto',
          }}
        >
          <BellSlashIcon size={18} color={theme.palette.text.disabled} />
          <Typography
            sx={{
              fontSize: 13,
              color: theme.palette.text.secondary,
              fontFamily: font(),
              lineHeight: 1.5,
              textAlign: 'left',
            }}
          >
            Enable notifications in your device settings to hear back when your request is approved.
          </Typography>
        </Box>
      );
    }

    // 'prompt' — show opt-in card
    return (
      <Box
        sx={{
          mb: 4,
          py: 2,
          px: 2.5,
          bgcolor: isDark ? 'rgba(108, 100, 253, 0.1)' : 'rgba(108, 100, 253, 0.07)',
          border: `1px solid ${isDark ? 'rgba(108, 100, 253, 0.25)' : 'rgba(108, 100, 253, 0.2)'}`,
          borderRadius: 3,
          maxWidth: 360,
          mx: 'auto',
          textAlign: 'left',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
          <BellIcon size={20} color={theme.palette.primary.main} />
          <Box>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontFamily: font(),
                mb: 0.5,
              }}
            >
              Get notified when you're approved
            </Typography>
            <Typography
              sx={{
                fontSize: 13,
                color: theme.palette.text.secondary,
                fontFamily: font(),
                lineHeight: 1.5,
              }}
            >
              Enable push notifications so you know the moment your request is accepted. You can turn them off anytime in Settings.
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={handleEnableNotifications}
          disabled={enablingNotifs}
          size="small"
          variant="contained"
          disableElevation
          sx={{
            textTransform: 'none',
            fontFamily: font(),
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 2,
            px: 2,
            ml: 0.5,
          }}
        >
          {enablingNotifs ? 'Enabling…' : 'Enable Notifications'}
        </Button>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: theme.palette.background.default,
        p: { xs: 2, sm: 3, md: 4 },
        textAlign: 'center',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: { xs: '100%', sm: 480, md: 560 }, mx: 'auto' }}>
        {/* Logo */}
        <Box sx={{ mb: { xs: 4, sm: 5 } }}>
          <Logo size="large" variant="default" state="slow-animated" />
        </Box>

        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            mb: 1.5,
            fontFamily: font(),
            fontSize: { xs: 22, sm: 26 },
          }}
        >
          You're on the List!
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: 14, sm: 16 },
            color: theme.palette.text.secondary,
            mb: 4,
            maxWidth: 380,
            mx: 'auto',
            lineHeight: 1.6,
            fontFamily: font(),
          }}
        >
          Thanks for your interest in Watchlists! We're carefully reviewing requests to ensure the best experience for our community.
        </Typography>

        {/* User Info Card */}
        <Box
          sx={{
            bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
            borderRadius: 3,
            p: 2,
            mb: 3,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
            maxWidth: 400,
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={user.photoURL || undefined}
              alt={user.displayName || 'User'}
              sx={{
                width: 56,
                height: 56,
                bgcolor: theme.palette.primary.main,
                fontSize: 24,
              }}
            >
              {user.displayName?.[0] || user.email?.[0] || 'U'}
            </Avatar>
            <Box sx={{ textAlign: 'left', flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: 16,
                  fontFamily: font(),
                }}
              >
                {user.displayName || 'User'}
              </Typography>
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: 14,
                  fontFamily: font(),
                }}
              >
                {user.email}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Push notification opt-in / status */}
        {renderNotificationSection()}

        {/* Sign Out */}
        <Box sx={{ mt: 2 }}>
          <Button
            onClick={onLogout}
            startIcon={<Logout />}
            sx={{
              color: theme.palette.text.secondary,
              textTransform: 'none',
              fontFamily: font(),
              fontWeight: 500,
              '&:hover': {
                color: theme.palette.primary.main,
                bgcolor: 'transparent',
              },
            }}
          >
            Sign Out
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AccessPendingScreen;
